import React, { useState } from 'react';

const InformationsGenerales: React.FC = () => {
  const [activeTab, setActiveTab] = useState('admission');

  const tabs = [
    { id: 'admission', label: 'Admission', icon: '📋' },
    { id: 'inscription', label: 'Inscription', icon: '📝' },
    { id: 'objectifs', label: 'Objectifs', icon: '🎯' },
    { id: 'organisation', label: 'Organisation', icon: '📅' },
    { id: 'duree', label: 'Durée & Programme', icon: '⏱️' },
    { id: 'etudes-ulterieures', label: 'Études Ultérieures', icon: '🎓' }
  ];

  return (
    <section id="informations-generales" className="py-16 bg-white dark:bg-gray-900 transition-colors duration-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Informations Générales
          </h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Tout ce que vous devez savoir sur l'admission, l'inscription et l'organisation des études à l'ISTMECH
          </p>
        </div>

        {/* Navigation par onglets */}
        <div className="flex flex-wrap justify-center mb-8 bg-gray-100 dark:bg-gray-800 rounded-xl p-2 transition-colors duration-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-4 py-3 m-1 rounded-lg font-medium transition-all duration-200 ${activeTab === tab.id
                ? 'bg-blue-600 text-white shadow-lg'
                : 'text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 hover:text-blue-600 dark:hover:text-blue-400'
                }`}
            >
              <span className="mr-2">{tab.icon}</span>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Contenu des onglets */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 transition-colors duration-200">
          {/* CONDITIONS D'ADMISSION */}
          {activeTab === 'admission' && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Conditions d'Admission
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-300">
                  Critères requis pour intégrer l'ISTMECH
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Admission en 1ère année */}
                <div className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg transition-colors duration-200">
                  <h4 className="text-xl font-bold text-blue-900 dark:text-blue-100 mb-4 flex items-center">
                    <span className="mr-3">🎓</span>
                    Admission en 1ère année
                  </h4>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong className="text-gray-900 dark:text-white">Certificat de Fin d'Études Secondaires</strong>
                        <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                          Deuxième Partie (Philosophie), délivré par le Ministère de l'Éducation Nationale
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong className="text-gray-900 dark:text-white">Diplôme étranger équivalent</strong>
                        <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                          L'équivalence doit être demandée au Ministère de l'Éducation Nationale
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong className="text-gray-900 dark:text-white">Examen d'admission</strong>
                        <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                          Réussir l'examen d'admission organisé par l'Institut
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>

                {/* Admission en année supérieure */}
                <div className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg transition-colors duration-200">
                  <h4 className="text-xl font-bold text-green-900 dark:text-green-100 mb-4 flex items-center">
                    <span className="mr-3">📈</span>
                    Admission en année supérieure
                  </h4>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong className="text-gray-900 dark:text-white">Certificat de réussite</strong>
                        <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                          1ère ou 2ème année d'enseignement supérieur d'une institution agréée par le Ministère de la Santé
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong className="text-gray-900 dark:text-white">Bulletin de notes</strong>
                        <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                          Accompagner le certificat du bulletin de notes détaillé
                        </p>
                      </div>
                    </li>
                  </ul>

                  <div className="mt-6 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-400 p-4">
                    <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                      <strong>Note :</strong> La direction se réserve le droit de la décision finale.
                      Des examens complémentaires peuvent être exigés.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* INSCRIPTIONS */}
          {activeTab === 'inscription' && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Procédure d'Inscription
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-300">
                  Modalités et horaires pour s'inscrire à l'ISTMECH
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Horaires et lieu */}
                <div className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg transition-colors duration-200">
                  <h4 className="text-xl font-bold text-blue-900 dark:text-blue-100 mb-6 flex items-center">
                    <span className="mr-3">🏢</span>
                    Lieu et Horaires
                  </h4>

                  <div className="space-y-4">
                    <div className="flex items-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <span className="text-2xl mr-4">📍</span>
                      <div>
                        <strong className="text-blue-900 dark:text-blue-100">Secrétariat de l'Institut</strong>
                        <p className="text-blue-700 dark:text-blue-200 text-sm">Hôpital Universitaire Justinien</p>
                      </div>
                    </div>

                    <div className="flex items-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <span className="text-2xl mr-4">🕘</span>
                      <div>
                        <strong className="text-green-900 dark:text-green-100">Lundi au Vendredi</strong>
                        <p className="text-green-700 dark:text-green-200 text-sm">09h00 à 14h00</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Procédure */}
                <div className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg transition-colors duration-200">
                  <h4 className="text-xl font-bold text-green-900 mb-6 flex items-center">
                    <span className="mr-3">📋</span>
                    Procédure
                  </h4>

                  <div className="space-y-4">
                    <div className="flex items-start">
                      <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-1">1</span>
                      <div>
                        <strong className="text-gray-900 dark:text-white">Dossier complet</strong>
                        <p className="text-gray-600 dark:text-gray-300 text-sm">Remettre le dossier administratif complet</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-1">2</span>
                      <div>
                        <strong className="text-gray-900 dark:text-white">Paiement</strong>
                        <p className="text-gray-600 dark:text-gray-300 text-sm">Effectuer le paiement des frais d'inscription</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-1">✓</span>
                      <div>
                        <strong className="text-gray-900 dark:text-white">Enregistrement</strong>
                        <p className="text-gray-600 dark:text-gray-300 text-sm">Inscription enregistrée immédiatement</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-400 p-6">
                <h5 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-2">📢 Informations Importantes</h5>
                <ul className="text-yellow-800 dark:text-yellow-200 space-y-1">
                  <li>• Les documents requis sont indiqués par voie de presse</li>
                  <li>• Les informations sont affichées à l'Institut et à la Direction Départementale de la Santé Publique</li>
                  <li>• Le coût des études peut être obtenu au secrétariat de l'Institut</li>
                </ul>
              </div>
            </div>
          )}

          {/* OBJECTIFS */}
          {activeTab === 'objectifs' && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Objectifs de la Formation
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-300">
                  Compétences et rôle du technologiste médical diplômé
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-700 dark:to-gray-600 rounded-2xl p-8">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-blue-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <span className="text-white text-3xl">🔬</span>
                  </div>
                  <h4 className="text-2xl font-bold text-blue-900 dark:text-blue-100 mb-4">
                    Profil du Diplômé
                  </h4>
                </div>

                <div className="prose prose-lg max-w-none text-gray-700 dark:text-gray-300 leading-relaxed">
                  <p className="text-center text-lg mb-8">
                    Le diplômé en Technologie Médicale acquiert les <strong className="text-blue-900 dark:text-blue-100">compétences théoriques
                      et technologiques</strong> nécessaires à la compréhension et à l'investigation de la physiologie humaine.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                  <div className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg transition-colors duration-200">
                    <h5 className="text-lg font-bold text-blue-900 dark:text-blue-100 mb-4 flex items-center">
                      <span className="mr-3">🎯</span>
                      Rôle Principal
                    </h5>
                    <p className="text-gray-700 dark:text-gray-300">
                      Maîtriser la <strong>mise au point, l'exécution et l'interprétation</strong> d'investigations
                      effectuées au sein de laboratoires d'analyses cliniques et de recherche biomédicale.
                    </p>
                  </div>

                  <div className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg transition-colors duration-200">
                    <h5 className="text-lg font-bold text-green-900 dark:text-green-100 mb-4 flex items-center">
                      <span className="mr-3">🧪</span>
                      Domaines d'Expertise
                    </h5>
                    <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                        Immunologie
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                        Biotechnologie
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                        Hématologie
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                        Bactériologie
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ORGANISATION */}
          {activeTab === 'organisation' && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Organisation des Études
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-300">
                  Calendrier académique et sessions d'examens
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Calendrier académique */}
                <div className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg transition-colors duration-200">
                  <h4 className="text-xl font-bold text-blue-900 dark:text-blue-100 mb-6 flex items-center">
                    <span className="mr-3">📅</span>
                    Calendrier Académique
                  </h4>

                  <div className="space-y-4">
                    <div className="flex items-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <span className="text-2xl mr-4">🎉</span>
                      <div>
                        <strong className="text-blue-900 dark:text-blue-100">Ouverture officielle</strong>
                        <p className="text-blue-700 dark:text-blue-200 text-sm">08 octobre de chaque année</p>
                      </div>
                    </div>

                    <div className="flex items-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <span className="text-2xl mr-4">📚</span>
                      <div>
                        <strong className="text-green-900 dark:text-green-100">Début des cours</strong>
                        <p className="text-green-700 dark:text-green-200 text-sm">Premier lundi du mois d'octobre</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sessions d'examens */}
                <div className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg transition-colors duration-200">
                  <h4 className="text-xl font-bold text-purple-900 dark:text-purple-100 mb-6 flex items-center">
                    <span className="mr-3">📝</span>
                    Sessions d'Examens
                  </h4>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <div className="flex items-center">
                        <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">1</span>
                        <span className="font-medium text-purple-900 dark:text-purple-100">Février</span>
                      </div>
                      <span className="text-purple-700 dark:text-purple-200 text-sm">Session ordinaire</span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <div className="flex items-center">
                        <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">2</span>
                        <span className="font-medium text-purple-900 dark:text-purple-100">Juillet</span>
                      </div>
                      <span className="text-purple-700 dark:text-purple-200 text-sm">Session ordinaire</span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                      <div className="flex items-center">
                        <span className="bg-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">3</span>
                        <span className="font-medium text-orange-900 dark:text-orange-100">Septembre</span>
                      </div>
                      <span className="text-orange-700 dark:text-orange-200 text-sm">Rattrapage</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cours isolés */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-6">
                <h4 className="text-xl font-bold text-indigo-900 dark:text-indigo-100 mb-4 flex items-center">
                  <span className="mr-3">📖</span>
                  Cours Isolés / Cours à Option
                </h4>
                <p className="text-indigo-800 dark:text-indigo-200">
                  L'ISTMECH envisage de mettre à disposition des étudiants des <strong>cours de culture générale</strong>
                  en tant que cours isolés ou cours à option. Ils seront annoncés par voie d'affiches.
                </p>
              </div>
            </div>
          )}

          {/* DURÉE & PROGRAMME */}
          {activeTab === 'duree' && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Durée des Études & Programme
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-300">
                  Formation de 4 ans alliant théorie et pratique
                </p>
              </div>

              {/* Durée générale */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-700 dark:to-gray-600 rounded-2xl p-8 text-center">
                <div className="flex justify-center items-center mb-6">
                  <div className="bg-blue-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mr-4">4</div>
                  <div className="text-left">
                    <div className="text-3xl font-bold text-blue-900 dark:text-blue-100">Années</div>
                    <div className="text-blue-700 dark:text-blue-200">de formation complète</div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-700 rounded-xl p-4 inline-block">
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">~40h</span>
                  <span className="text-gray-600 dark:text-gray-300 ml-2">par semaine</span>
                </div>
                <p className="text-blue-800 dark:text-blue-200 mt-4 font-medium">
                  Programme associant étroitement <strong>théorie et pratique</strong>
                </p>
              </div>

              {/* Programme par année */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* 1ère année */}
                <div className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg border-t-4 border-green-500">
                  <div className="text-center mb-4">
                    <div className="bg-green-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-2">1</div>
                    <h4 className="text-xl font-bold text-green-900 dark:text-green-100">Première Année</h4>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                    <h5 className="font-semibold text-green-900 dark:text-green-100 mb-2">Formation Générale</h5>
                    <p className="text-green-800 dark:text-green-200 text-sm">
                      Cours fondamentaux pour acquérir les bases théoriques essentielles
                      en sciences médicales et biologiques.
                    </p>
                  </div>
                </div>

                {/* 2ème année */}
                <div className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg border-t-4 border-blue-500">
                  <div className="text-center mb-4">
                    <div className="bg-blue-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-2">2</div>
                    <h4 className="text-xl font-bold text-blue-900 dark:text-blue-100">Deuxième Année</h4>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <h5 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Spécialisation</h5>
                    <p className="text-blue-800 dark:text-blue-200 text-sm">
                      Cours spécialisés accompagnés d'une formation pratique poussée
                      dans les différentes disciplines.
                    </p>
                  </div>
                </div>

                {/* 3ème année */}
                <div className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg border-t-4 border-purple-500">
                  <div className="text-center mb-4">
                    <div className="bg-purple-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-2">3</div>
                    <h4 className="text-xl font-bold text-purple-900 dark:text-purple-100">Troisième Année</h4>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                    <h5 className="font-semibold text-purple-900 dark:text-purple-100 mb-2">Approfondissement & Applications</h5>
                    <p className="text-purple-800 dark:text-purple-200 text-sm">
                      Approfondissement théorique et pratique dans les disciplines de biologie médicale,
                      préparation aux stages professionnels.
                    </p>
                  </div>
                </div>

                {/* 4ème année */}
                <div className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg border-t-4 border-indigo-500">
                  <div className="text-center mb-4">
                    <div className="bg-indigo-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-2">4</div>
                    <h4 className="text-xl font-bold text-indigo-900 dark:text-indigo-100">Quatrième Année</h4>
                  </div>
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4">
                    <h5 className="font-semibold text-indigo-900 dark:text-indigo-100 mb-2">Stages Professionnels</h5>
                    <p className="text-indigo-800 dark:text-indigo-200 text-sm">
                      Stages intensifs en laboratoires de Biologie Médicale, milieux universitaires
                      et, éventuellement, en entreprise, en vue de l'intégration professionnelle.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ÉTUDES ULTÉRIEURES */}
          {activeTab === 'etudes-ulterieures' && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Possibilités d'Études Ultérieures
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-300">
                  Poursuivez votre parcours académique après l'ISTMECH
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Licence */}
                <div className="bg-gradient-to-br from-green-400 to-green-600 text-white rounded-xl p-8 text-center transform hover:scale-105 transition-transform duration-200">
                  <div className="text-4xl mb-4">🎓</div>
                  <h4 className="text-2xl font-bold mb-2">Licence</h4>
                  <p className="text-green-100">
                    Premier cycle universitaire pour approfondir vos connaissances
                  </p>
                </div>

                {/* Maîtrise */}
                <div className="bg-gradient-to-br from-blue-400 to-blue-600 text-white rounded-xl p-8 text-center transform hover:scale-105 transition-transform duration-200">
                  <div className="text-4xl mb-4">📚</div>
                  <h4 className="text-2xl font-bold mb-2">Maîtrise</h4>
                  <p className="text-blue-100">
                    Deuxième cycle pour une spécialisation avancée
                  </p>
                </div>

                {/* Doctorat */}
                <div className="bg-gradient-to-br from-purple-400 to-purple-600 text-white rounded-xl p-8 text-center transform hover:scale-105 transition-transform duration-200">
                  <div className="text-4xl mb-4">🔬</div>
                  <h4 className="text-2xl font-bold mb-2">Doctorat</h4>
                  <p className="text-purple-100">
                    Troisième cycle pour la recherche et l'expertise
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-8 text-center">
                <h4 className="text-2xl font-bold text-indigo-900 dark:text-indigo-100 mb-4">
                  🚀 Votre Avenir Professionnel
                </h4>
                <p className="text-indigo-800 dark:text-indigo-200 text-lg leading-relaxed">
                  Avec un diplôme de l'ISTMECH, vous avez accès à de nombreuses opportunités
                  de formation continue et de spécialisation dans le domaine de la technologie médicale,
                  tant en Haïti qu'à l'international.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default InformationsGenerales;
